import { useEffect, useRef, useState } from 'react'
import { POLL } from '@/lib/pollTiers'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useCanOpenModule } from '@/hooks/useCanOpenModule'
import { supabase } from '@/lib/supabase'
import { PACKING_OPEN_STATUSES } from '@/lib/runStatus'
import QueryStateGate from '@/components/QueryStateGate'
import { readState } from '@/lib/readState'
import { sastTodayYMD } from '@/lib/dates'
import { useSaveDb, ensureSaveDb } from '@/lib/useSaveDb'
import { ValidationError } from '@/lib/errorReporter'
import { friendlyError } from '@/lib/friendlyError'
import { REJECT_BIN_DOMAIN } from '@/lib/packhouseErrorDomains'
import { updateWithVersion, isStaleWriteError } from '@/lib/optimistic'
import { insertOrQueueReject } from '@/lib/offlineQueue'
import { safeDecimal } from '@/lib/numericInput'
import Photo from '@/components/Photo'
import type { Run } from '@/types/database'
import PickRunFirst from '@/components/PickRunFirst'
import { CurrentLotChip } from '@/components/CurrentLotChip'
import { useCurrentLot } from '@/hooks/useCurrentLot'
import { RunStatusBadge } from '@/components/RunSelector'
import { confirmDialog, alertDialog } from '@/components/Dialogs'
import { useTenantPersistedState } from '@/hooks/useTenantPersistedState'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { isAdmin } from '@/lib/roles'
import SubmitOverlay from '@/components/SubmitOverlay'
import Modal from '@/components/Modal'
import ContainerTypePicker, {
  decodeContainerPickerValue, encodeEnum,
  type ContainerPickerValue,
} from '@/components/ContainerTypePicker'
import { CRATE_TYPES, type ContainerType } from '@/lib/containerTypes'
import { useBinTypes } from '@/hooks/useBinTypes'
import { resolveTareKg } from '@/lib/tareResolver'
import { DOLLY_TARE_KEY, DOLLY_BLOCK_MESSAGE, dollyNet, dollySum, parseDollyTareKg, splitStoredTare } from '@/lib/dollyWeigh'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { useFlowManifest } from '@/hooks/useCommodityTemplates'
import { REJECT_SOURCES, BERRY_REJECT_SOURCES, sourceChip, rejectBreakdownSources } from '@/lib/rejectSource'
import ComboField from '@/components/ComboField'
import TagInput from '@/components/TagInput'
import {
  Plus, Camera, AlertTriangle, Trash2, Pencil, Package,
  CheckCircle,
} from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import { Button, Card } from '@/components/ui'
import CaptureFab from '@/components/CaptureFab'
import clsx from 'clsx'
import { useFieldIssues } from '@/hooks/useFieldIssues'
import { BrandLoader } from '@/components/BrandLoader'
import SavedBanner, { SavedBannerButton } from '@/components/SavedBanner'
import MutationOverlays, { onlyWhenDone } from '@/components/MutationOverlays'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
// RejectSource / REJECT_SOURCES / BERRY_REJECT_SOURCES / sourceChip now live in
// @/lib/rejectSource (imported above), shared with Runs.tsx (M71).

type Disposition = 'PENDING' | 'STALL' | 'FARMER_COLLECT' | 'MAGALIES' | 'DUMPED'

interface RejectEntry {
  reject_id:      string
  run_id:         string
  reject_source:  string | null   // citrus enum OR a berry stage (mig 1046 — free text)
  kind:           'REJECT' | 'WASTE'
  weight_kg:      number          // net weight (mig 015 — operator-entered or gross − tare)
  gross_weight_kg: number | null  // raw scale reading when gross-mode was used (mig 285)
  tare_kg:         number | null  // bin_count × per-bin tare subtracted to get net
  bin_count:      number | null
  defect_reasons: string[] | null
  notes:          string | null
  photo_url:      string | null
  logged_at:      string
  reject_bin_id:  string | null   // points at the active reject_bins row this entry was added to
  version:        number          // mig 344 — for updateWithVersion on disposition / edit
  // Mig 167 disposition columns.
  disposition:                Disposition
  disposition_at:             string | null
  disposition_notes:          string | null
  transferred_to_steel_cage:  boolean
  // Joined from reject_bins so the row can show the operator-readable
  // bin number ("RJ-001") instead of just the UUID.
  reject_bins?:  { bin_number: string } | null
}

const DISPOSITION_CFG: Record<Disposition, { label: string; emoji: string; pill: string }> = {
  PENDING:        { label: 'Pending',         emoji: '⏳', pill: 'bg-amber-900/40 text-amber-300 border-amber-700/40'   },
  STALL:          { label: 'Stall',           emoji: '🏪', pill: 'bg-blue-900/40 text-blue-300 border-blue-700/40'      },
  FARMER_COLLECT: { label: 'Farmer collect',  emoji: '👨‍🌾', pill: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40' },
  MAGALIES:       { label: 'Magalies (juice)', emoji: '🥤', pill: 'bg-purple-900/40 text-purple-300 border-purple-700/40' },
  DUMPED:         { label: 'Dumped',          emoji: '🗑', pill: 'bg-surface text-fg-muted border-line'             },
}

const DEFECT_REASONS = [
  'Rind breakdown', 'Red scale', 'Creasing', 'Dark marks',
  'Hail marks', 'Wind scars', 'Oleo', 'Fruit fly', 'Missing button', 'Mould',
]

// ---------------------------------------------------------------------------
// Berry (force-cool) reject flow — mig 1046 opened reject_source to free text.
// Berries reject at Field / Packline (+ operator-added stages), captured in
// CRATES (crate count × crate tare → net, reusing the gross-weight machinery),
// NOT the citrus MAF/Presort/Packer + citrus-defect model. Everything below is
// gated on useFlowManifest().usesForceCooling so citrus is byte-for-byte
// unchanged. BERRY_REJECT_SOURCES + sourceChip now live in @/lib/rejectSource
// (imported above), shared with Runs.tsx (M71).
// ---------------------------------------------------------------------------

// The berry crate container for a commodity — drives the reject bin's tare so
// net = gross − crates × crate_tare (the same tare source Precool/intake use).
function berryCrateFor(commodityName: string | null): 'BLUEBERRY_CRATE' | 'STRAWBERRY_CRATE' {
  return (commodityName ?? '').toUpperCase().includes('STRAWBERR') ? 'STRAWBERRY_CRATE' : 'BLUEBERRY_CRATE'
}

// Berry reject-source suggestions = the two seeds + any operator-added source
// already logged (so "add more" persists across sessions). Non-citrus values
// only; tenant-scoped by RLS. Cheap + cached — the ComboField still allows any
// typed value, this only powers autocomplete.
function useBerryRejectSources(enabled: boolean) {
  return useQuery({
    queryKey: ['berry-reject-sources'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('reject_log')
        .select('reject_source')
        .is('deleted_at', null)
        .not('reject_source', 'is', null)
        .not('reject_source', 'in', '("PRESORTER","MAF_RODA","PACKER")')
        .limit(1000)
      if (error) throw error
      const used = ((data ?? []) as { reject_source: string | null }[])
        .map(r => r.reject_source).filter(Boolean) as string[]
      return Array.from(new Set([...BERRY_REJECT_SOURCES, ...used])).sort()
    },
  })
}

// YYYY-MM-DD (local) + the current time-of-day → ISO timestamp. Used by
// the admin backdate so the row sorts into the correct historical day
// in the user's timezone without flipping to UTC midnight (which would
// land on "yesterday" in SAST). Returns null on invalid input.
function combineLocalDateWithNowTime(yyyyMmDd: string): string | null {
  const [y, m, d] = yyyyMmDd.split('-').map(Number)
  if (!y || !m || !d) return null
  const now = new Date()
  const out = new Date(now)
  out.setFullYear(y, m - 1, d)
  return out.toISOString()
}

// ---------------------------------------------------------------------------
// New Reject Form — standalone sub-view
// ---------------------------------------------------------------------------
// REJ-36 — the Save refusal when NEW_BIN is picked over an open-bin list the
// form never read. Worded without "connection"/"network" so the save card does
// not add its "Looks transient — try again" hint: nothing was saved, and the
// way forward is loading the list, not repeating the tap.
const NEW_BIN_UNREAD_MESSAGE =
  "This run's open bins haven't loaded, so a new bin can't be started yet — it could duplicate one that is already open. " +
  'Tap Try again under Bin, or pick an open bin once the list shows.'

// U05 UF-04 — what the Log form hands back so the list can show the Done banner.
type LoggedReject = {
  queued: boolean
  /** The bin the weighing went into; null for "Don't bin". */
  binId: string | null
  kg: number
  kind: string
  /** F06 UF-01: the save also closed the bin (Full, ready to send). */
  closed?: boolean
  /** F06 UF-01: the entry saved but the close did not. The bin is still open. */
  closeFailed?: boolean
  /** F06 UF-01: Full was chosen but the save was queued; the bin stays open. */
  fullQueued?: boolean
}

type CloseableBin = { reject_bin_id: string; bin_number: string; weight_kg: number; version: number }

// Close an OPEN bin: OPEN → READY, stamp closed_at, insert a CLOSED event.
// Shared by the Log form's bin picker and the Done banner.
// F06 UF-01: `ask: false` skips the confirm when the operator already chose
// "Full, ready to send" on the Log form.
async function closeRejectBinNow(bin: CloseableBin, opts: { ask?: boolean } = {}) {
  if (opts.ask !== false) {
    const ok = await confirmDialog({
      title:   `Close bin ${bin.bin_number}?`,
      message: `${bin.weight_kg.toFixed(1)} kg will move to READY and become pickable for stall / juice / collect / dispose.`,
      confirm: 'Close bin',
      tone:    'warning',
    })
    if (!ok) throw new ValidationError('cancelled')
  }
  const { data: { session } } = await supabase.auth.getSession()
  const userId = session?.user?.id ?? null
  try {
    await updateWithVersion(
      'reject_bins',
      { col: 'reject_bin_id', value: bin.reject_bin_id },
      bin.version,
      { status: 'READY', closed_at: new Date().toISOString() },
    )
  } catch (e) {
    if (isStaleWriteError(e)) {
      throw new ValidationError('This bin was updated by someone else — refresh and try again.')
    }
    throw e
  }
  const { error: evErr } = await supabase.from('reject_bin_events').insert({
    reject_bin_id: bin.reject_bin_id,
    kind:          'CLOSED',
    weight_kg:     bin.weight_kg,
    happened_by:   userId,
  })
  if (evErr) throw evErr
}

// true = the bin was closed, false = the operator backed out of the confirm.
// Feed the mutation to onlyWhenDone so a declined confirm shows no tick.
async function closeRejectBinAsked(bin: CloseableBin): Promise<boolean> {
  try { await closeRejectBinNow(bin) } catch (e) {
    if (e instanceof Error && e.message === 'cancelled') return false
    throw e
  }
  return true
}

// ---------------------------------------------------------------------------
// Berry waste weighed on a dolly. The scale reads crates + fruit + the empty
// dolly; the dolly's weight is a per-business setting (system_config
// `berry_dolly_tare_kg`, Config → Berry intake). Same key + row shape as
// SystemConfigSection, so a Config save refreshes this read. The toggle is
// remembered per device. Berry + gross mode + new bin only: every other path
// (citrus, net entry, an existing bin) never sees it. The net/tare rule is the
// pure dollyNet() in @/lib/dollyWeigh.
// ---------------------------------------------------------------------------
// fallow-ignore-next-line complexity
function useDollyWeigh({ eligible, grossKg, crates, crateTareKg }: {
  eligible: boolean; grossKg: string; crates: string; crateTareKg: number
}) {
  const [wanted, setWanted] = usePersistedState('rejects.weighedOnDolly', false)
  const on = eligible && wanted
  const tenantId = useActiveTenantId()
  const cfgQ = useQuery({
    queryKey: ['sys-config', tenantId, DOLLY_TARE_KEY],
    enabled: !!tenantId && on,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('system_config')
        .select('config_key, config_value')
        .eq('tenant_id', tenantId)
        .in('config_key', [DOLLY_TARE_KEY])
      if (error) throw error
      return data as { config_key: string; config_value: string }[]
    },
  })
  const tareKg = parseDollyTareKg(cfgQ.data?.find(r => r.config_key === DOLLY_TARE_KEY)?.config_value)
  const result = dollyNet({
    grossKg: parseFloat(grossKg) || 0,
    crates: parseInt(crates || '0', 10) || 0,
    crateTareKg, dollyTareKg: tareKg,
  })
  const loading = cfgQ.isLoading
  // Save is held while the dolly weight is unknown (loading, failed or unset).
  const blocked = on && (loading || cfgQ.isError || tareKg === null)
  return { wanted, setWanted, on, tareKg, result, loading, isError: cfgQ.isError, blocked }
}

// The "Weighed on a dolly" switch + the live arithmetic under the scale box.
// fallow-ignore-next-line complexity
function DollyPanel({ dolly, grossKg, crates, crateTareKg }: {
  dolly: ReturnType<typeof useDollyWeigh>; grossKg: string; crates: string; crateTareKg: number
}) {
  const { result, tareKg } = dolly
  const gross = parseFloat(grossKg) || 0
  const n = parseInt(crates || '0', 10) || 0
  let note: React.ReactNode = null
  if (dolly.on) {
    if (dolly.loading) note = <p className="text-caption text-fg-muted mt-1">Loading the empty dolly weight…</p>
    else if (dolly.isError) note = <p className="text-caption text-amber-300 mt-1" role="alert">The empty dolly weight did not load. Try again before you log.</p>
    else if (tareKg === null) note = <p className="text-caption text-amber-300 mt-1" role="alert">{DOLLY_BLOCK_MESSAGE.no_dolly_tare}</p>
    else if (result.ok) note = <p className="text-caption text-emerald-300 font-semibold mt-1 tabular-nums">{dollySum(gross, n, crateTareKg, tareKg, result.netKg)}</p>
    else if (gross > 0 || n > 0) note = <p className="text-caption text-amber-300 mt-1" role="status">{DOLLY_BLOCK_MESSAGE[result.reason]}</p>
  }
  return (
    <div className="mt-2">
      <button type="button" aria-pressed={dolly.wanted}
        onClick={() => dolly.setWanted(v => !v)}
        className={clsx('tap-target-y text-caption uppercase tracking-wider px-2 py-0.5 rounded transition-colors',
          dolly.wanted ? 'bg-emerald-900/50 text-emerald-300' : 'bg-raised text-fg-secondary hover:brightness-110')}
        title="The scale reading includes the whole dolly: crates, fruit and the empty dolly">
        {dolly.wanted ? 'Weighed on a dolly: on' : 'Weighed on a dolly: off'}
      </button>
      {note}
    </div>
  )
}

function NewRejectForm({ run, onDone, runClosed = false, startOn = null, newerRun = null }: {
  newerRun?: Run | null
  run: Run; onDone: (logged?: LoggedReject) => void; runClosed?: boolean
  /** F06 UF-02: "Add weighing" on the Filling now strip opens the form on that bin. */
  startOn?: { binId: string; kind: 'REJECT' | 'WASTE' } | null
}) {
  const qc = useQueryClient()
  const cameraRef = useRef<HTMLInputElement>(null)
  const { data: me } = useCurrentUser()
  const meIsAdmin = isAdmin(me?.role)
  // Isolated client for the multi-step save — bypasses navigator.locks
  // contention with the Rejects page's other useQuery hooks. See
  // src/lib/useSaveDb.ts.
  const saveDb = useSaveDb()
  const manifest = useFlowManifest()
  const isBerry  = manifest.usesForceCooling
  const { data: berrySourceOpts = BERRY_REJECT_SOURCES } = useBerryRejectSources(isBerry)

  // Berry reject_source is free text (Field/Packline/typed); citrus sets one of
  // the three fixed enum values via chips. One string state serves both.
  const [source,       setSource]       = useState<string>('')
  const [kind,         setKind]         = useState<'REJECT' | 'WASTE'>(startOn?.kind ?? 'REJECT')
  const [weightKg,     setWeightKg]     = useState('')
  const [binCount,     setBinCount]     = useState('')
  // Cartons condemned unopened off a received pallet (mig 20261019110000) —
  // a count beside the kg; the bin is weighed whole so the kg already holds them.
  const [wholeCartons, setWholeCartons] = useState('')
  const [defects,      setDefects]      = useState<string[]>([])
  const [notes,        setNotes]        = useState('')
  const [photoFile,    setPhotoFile]    = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [error,        setError]        = useState<string | null>(null)
  // Admin-only backdate. Empty string = use server NOW(). Operators
  // never see this control — typing a date here is a power-user
  // affordance for logging historical / paper-trail data.
  const [backdate,     setBackdate]     = useState('')
  // Mig 285 — gross-weight entry. Operator weighs gross (N bins on a
  // scale = N × bin_tare + fruit); the system subtracts bin_count ×
  // per-bin tare to get net. Operator-confirmed flow 2026-05-11:
  // "I always weigh gross and we tare with the system". Defaults ON
  // since that's the standard practice; toggle stays for rare edge
  // cases (e.g. weighing on a calibrated tareing scale already).
  const [grossMode,    setGrossMode]    = useState(true)
  const [grossKg,      setGrossKg]      = useState('')
  // Bin assignment: pick an existing OPEN bin matching the kind, or
  // create a new one. NEW_BIN is the default so end-of-line filling is
  // friction-free.
  const [binChoice,    setBinChoice]    = useState<string>(startOn?.binId ?? 'NEW_BIN')
  // F06 UF-01: a new bin is "Full, ready to send" unless the operator says
  // "Still filling". Its own state: a Kind tap resets the bin choice but must
  // not re-arm Full once Still filling was picked.
  const [fullBin,      setFullBin]      = useState(true)
  // Which LOT (tipped pallet / delivery) this reject came from — mig
  // 20261019100000. Defaults to the run's latest tip; NULL when none.
  const lot = useCurrentLot(run.run_id)
  // Required when binChoice === 'NEW_BIN'. Encoded value from ContainerTypePicker
  // — the picker merges the system enum with grower-specific bin_types from
  // bin inventory. Decoded on save into (container_type, bin_type_id).
  // Berries collect rejects into a CRATE (so the tare deducts a crate, not a
  // 37 kg citrus bin); citrus defaults to a KF49 bin. Operator can still change it.
  const [newBinPick, setNewBinPick] = useState<ContainerPickerValue>(encodeEnum(isBerry ? berryCrateFor(manifest.commodityName) : 'KF49_BIN'))
  const runGrowerId = (run as Run & { grower_id?: string | null }).grower_id ?? null
  const { data: customTypes = [], isLoading: binTypesLoading, isError: binTypesError, refetch: refetchBinTypes } = useBinTypes({ onlyActive: true, growerId: runGrowerId })

  // Open bins for this run + kind — eligible to add weight to. Fetches
  // `version` so the close-bin action below can run through
  // updateWithVersion (matches the reject_bins action panel on the
  // detail page — keeps the two paths consistent).
  const openBinsQ = useQuery({
    queryKey: ['reject-bins-open', run.run_id, kind],
    queryFn: async () => {
      const { data, error } = await supabase.from('reject_bins')
        .select('reject_bin_id, bin_number, weight_kg, version')
        .eq('run_id', run.run_id).eq('kind', kind).eq('status', 'OPEN')
        .is('deleted_at', null).order('created_at')
      if (error) throw error
      return data as { reject_bin_id: string; bin_number: string; weight_kg: number; version: number }[]
    },
  })
  const openBins = openBinsQ.data ?? []
  const refetchOpenBins = openBinsQ.refetch
  // REJ-36 / F06 D2 — "no open bins" and "we hold no open-bin list" look the
  // same on screen. A read that never landed (failed, PAUSED offline — queries
  // don't run without a network — or still loading after a Kind switch) is NOT
  // an empty list: a new bin started over it queues, and the drain would make
  // a second RB- bin beside one that is already open. So NEW_BIN is refused
  // until the list is read; a read that dropped AFTER it landed keeps its data.
  const openBinsState = readState(openBinsQ)
  const openBinsUnread = openBinsState !== 'usable'
  const openBinsOffline = openBinsState === 'unread' && openBinsQ.isPaused
  // A bin another tablet closed drops out of the re-read list; don't leave the
  // choice pointing at it (nothing would show as picked, and Log would send to
  // it again).
  useEffect(() => {
    if (openBinsQ.data && binChoice !== 'NEW_BIN' && binChoice !== 'NONE'
        && !openBinsQ.data.some(b => b.reject_bin_id === binChoice)) {
      setBinChoice('NEW_BIN')
    }
  }, [openBinsQ.data, binChoice])

  // Close an OPEN bin from the picker. Same shape as the bin detail
  // page's ActionsPanel close: flip status OPEN → READY, stamp
  // closed_at, insert a CLOSED event. updateWithVersion guards against
  // two operators racing on the same bin (e.g. one closing while
  // another logs more weight).
  const [closeError, setCloseError] = useState<string | null>(null)
  const closeBin = useMutation({
    mutationFn: async (bin: { reject_bin_id: string; bin_number: string; weight_kg: number; version: number }) => {
      setCloseError(null)
      return closeRejectBinAsked(bin)
    },
    onSuccess: (done, bin) => {
      if (!done) return
      // If the operator had this bin selected, fall back to NEW_BIN.
      if (binChoice === bin.reject_bin_id) setBinChoice('NEW_BIN')
      qc.invalidateQueries({ queryKey: ['reject-bins-open', run.run_id] })
      qc.invalidateQueries({ queryKey: ['reject-bins'] })
    },
    onError: (e: Error) => setCloseError(friendlyError(e, REJECT_BIN_DOMAIN).message),
  })

  function toggleDefect(d: string) {
    setDefects(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])
  }

  // Mig 285 — when grossMode is ON and a new bin is being created, resolve
  // the tare from the chosen container so we can compute net = gross - tare.
  // We only support gross-mode on NEW_BIN; existing bins keep their stored
  // tare via the reject_bins.bin_type_id snapshot (handled by the trigger
  // that sums weight onto the bin).
  const decodedNewBin    = decodeContainerPickerValue(newBinPick, customTypes)
  const resolvedTareKg   = resolveTareKg({
    containerType: decodedNewBin.container_type,
    binTypeId:     decodedNewBin.bin_type_id,
    binTypes:      customTypes,
  })
  // Per-bin tare. Operator-confirmed semantic: weighing N bins on the
  // scale at once means the scale shows N × per_bin_tare on top of the
  // fruit. So net = gross - (bin_count × tare). Defaults to 1 bin
  // when bin_count is blank.
  const perBinTareKg     = resolvedTareKg ?? 0
  const binsForCalc      = Math.max(1, parseInt(binCount || '1', 10) || 1)
  const totalTareKg      = perBinTareKg * binsForCalc
  // U05-D2: gross is never saved as net. With no tare known (still loading,
  // failed to load, or no default row for this container) the net is unknown.
  const tareUnknown      = resolvedTareKg === null
  const grossBlocked     = grossMode && binChoice === 'NEW_BIN' && tareUnknown
  const computedNetKg    = grossMode ? Math.max(0, (parseFloat(grossKg) || 0) - totalTareKg) : null
  const dolly = useDollyWeigh({
    eligible: isBerry && grossMode && binChoice === 'NEW_BIN', grossKg, crates: binCount, crateTareKg: perBinTareKg,
  })

  // U05 UF-01 — Log stays tappable; a tap with a gap marks it, scrolls to it
  // and names it. Gross mode + NEW_BIN reads grossKg, else weightKg (a net
  // entry for an existing bin or with gross mode off).
  const weightMissing = grossMode && binChoice === 'NEW_BIN' ? !grossKg : !weightKg
  const issues = useFieldIssues<'source' | 'weight'>([
    ...(!source ? [{ field: 'source' as const, message: 'Pick a Reject Source' }] : []),
    ...(weightMissing ? [{
      field: 'weight' as const,
      message: grossMode && binChoice === 'NEW_BIN' ? 'Enter the gross weight' : 'Enter the weight',
    }] : []),
  ])

  const logMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession(); const user = session?.user
      if (!user) throw new ValidationError('Not authenticated')
      const db = saveDb ?? await ensureSaveDb()
      if (!db) throw new ValidationError('Session not ready — refresh and try again.')
      if (!source) throw new ValidationError('Select a reject source')

      // Gross-mode: operator entered the scale reading; system applies
      // bin_count × per-bin tare. Only meaningful on NEW_BIN since
      // existing bins keep their snapshotted tare via the trigger.
      const useGross  = grossMode && binChoice === 'NEW_BIN'
      const gross     = useGross ? parseFloat(grossKg) : NaN
      const dollyRes  = useGross && dolly.on ? dolly.result : null
      const totalTare = useGross ? (dollyRes?.ok ? dollyRes.tareKg : totalTareKg) : 0
      const kg        = useGross
        ? (dollyRes?.ok ? dollyRes.netKg : Math.max(0, gross - totalTare))
        : parseFloat(weightKg)

      if (useGross && tareUnknown) {
        throw new ValidationError(binTypesError
          ? 'Bin types did not load, so the tare is not known. Try again before you log.'
          : 'No tare is set for this container. Turn off Weighed with bin and enter the net weight, or ask an admin to set it under Config → Storage → Bin types.')
      }
      if (dollyRes && !dollyRes.ok) {
        throw new ValidationError(dolly.isError
          ? 'The empty dolly weight did not load. Try again before you log.'
          : DOLLY_BLOCK_MESSAGE[dollyRes.reason])
      }
      if (useGross && (!gross || gross <= 0)) {
        throw new ValidationError('Enter gross weight')
      }
      if (useGross && kg <= 0) {
        throw new ValidationError(`Gross ${gross.toFixed(1)} kg ≤ total tare ${totalTare.toFixed(1)} kg (${binsForCalc} × ${perBinTareKg.toFixed(1)}) — net would be zero`)
      }
      if (!useGross && (!kg || kg <= 0)) {
        throw new ValidationError('Enter reject weight')
      }

      // Admin-only backdate. Validate before any insert so we don't
      // half-create a bin then reject the log row.
      const backdateIso = backdate && meIsAdmin
        ? combineLocalDateWithNowTime(backdate)
        : null
      if (backdate && !meIsAdmin) {
        throw new ValidationError('Only admins can backdate a reject.')
      }
      if (backdate && !backdateIso) {
        throw new ValidationError('Invalid date — use the picker.')
      }

      // Resolve the bin: an existing OPEN bin is just a reference; a NEW
      // bin travels WITH this entry (F06 D2). The helper mints its id on the
      // device and writes the bin before the entry, live or from the offline
      // queue, so the default path saves in a wifi drop too. The server's
      // trigger assigns the RB number when the bin lands.
      let binId: string | null = null
      let newBin: Record<string, unknown> | null = null
      if (binChoice === 'NEW_BIN' && openBinsUnread) {
        throw new ValidationError(NEW_BIN_UNREAD_MESSAGE)
      }
      if (binChoice === 'NEW_BIN') {
        newBin = {
          run_id:         run.run_id,
          grower_id:      runGrowerId,
          grower_name:    run.grower,
          variety:        run.variety,
          kind,
          container_type: decodedNewBin.container_type,
          bin_type_id:    decodedNewBin.bin_type_id,
          // How many physical bins this record holds. The trigger recomputes
          // it from the live reject_log rows anyway, but the log below can be
          // queued offline — set it here so the bin doesn't read "1 bin"
          // until the queue drains.
          bin_count:      binsForCalc,
          created_by:     user.id,
          // Admin backdate flows down to the bin's created_at so the
          // bin sorts into the right historical bucket in the list view.
          ...(backdateIso ? { created_at: backdateIso } : {}),
        }
      } else if (binChoice && binChoice !== 'NONE') {
        binId = binChoice
      }

      // Hand the bin (if new) + log insert to the offline queue helper.
      // Online path uploads the photo to Storage + inserts the rows
      // immediately; offline path stashes them in IndexedDB as ONE entry and
      // the drainer flushes on reconnect. "Don't bin" (no bin at all) saves
      // online only: the helper refuses to queue it and says so in plain
      // words (F06 D1).
      const result = await insertOrQueueReject({
        db: db,
        new_bin: newBin,
        payload: {
          run_id:         run.run_id,
          reject_source:  source,
          kind,
          weight_kg:      kg,
          // Mig 285 — only populated when the operator entered GROSS.
          gross_weight_kg: useGross ? gross : null,
          tare_kg:         useGross ? totalTare : null,
          bin_count:      binCount ? parseInt(binCount) : null,
          defect_reasons: defects.length > 0 ? defects : null,
          notes:          notes.trim() || null,
          reject_bin_id:  binId,
          logged_by:      user.id,
          tipping_id:     lot.effectiveTippingId,
          whole_cartons:  lot.effective?.lot_key.startsWith('pallet:') && wholeCartons ? parseInt(wholeCartons, 10) : null,
          // Admin backdate — same ISO timestamp used on the bin's
          // created_at above, so a backdated reject + its bin sort
          // together in the historical reports.
          ...(backdateIso ? { logged_at: backdateIso } : {}),
        },
        photo:      photoFile,
        photo_name: photoFile?.name ?? null,
      })
      const outBinId = (newBin?.reject_bin_id as string | undefined) ?? binId
      // F06 UF-01: Full → the same save closes the bin. Only after the entry
      // landed live; a queued entry leaves the bin open (it does not exist yet).
      let closed = false
      let closeFailed = false
      if (newBin && fullBin && result === 'sent' && outBinId) {
        try {
          const { data: b, error: bErr } = await supabase.from('reject_bins')
            .select('reject_bin_id, bin_number, weight_kg, version')
            .eq('reject_bin_id', outBinId).maybeSingle()
          if (bErr || !b) throw bErr ?? new Error('bin not read')
          await closeRejectBinNow(b as CloseableBin, { ask: false })
          closed = true
        } catch {
          closeFailed = true
        }
      }
      return { result, binId: outBinId, kg, closed, closeFailed, fullQueued: !!newBin && fullBin && result === 'queued' }
    },
    onSuccess: ({ result, binId, kg, closed, closeFailed, fullQueued }) => {
      qc.invalidateQueries({ queryKey: ['rejects', run.run_id] })
      // ['runs'] prefix-matches the run-selection dropdown too (mig away
      // from the old separate ['run-selection'] key on 2026-05-06).
      qc.invalidateQueries({ queryKey: ['runs'] })
      qc.invalidateQueries({ queryKey: ['reject-bins'] })
      qc.invalidateQueries({ queryKey: ['reject-bins-open'] })
      if (result === 'queued') {
        // The reject is captured locally + will land when network returns;
        // don't navigate away so the operator can see the queued state if
        // a banner / badge wants to show it.
        setError(null)
      }
      onDone({ queued: result === 'queued', binId, kg, kind, closed, closeFailed, fullQueued })
    },
    onError: (e: Error) => {
      setError(friendlyError(e, REJECT_BIN_DOMAIN).message)
      // F06 D4 — the server refuses an entry onto a bin another tablet has
      // since closed or released. Re-read the open bins so that bin drops out
      // of the picker before the operator picks again.
      void refetchOpenBins()
    },
  })

  // F06 UF-17: a form opened from a bin's "Add a weighing" scrolls the chosen
  // bin into view instead of leaving the operator at the Kind chips.
  const binSectionRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (startOn) binSectionRef.current?.scrollIntoView?.({ block: 'center' })
    // once, on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div ref={issues.formRef} className="min-h-full bg-canvas flex flex-col">
      {/* PW-13: one pinned row — the run line truncates under the title
          instead of wrapping the header to three rows (157px with the keyboard
          up, packhouse audit PM-PRD-25). */}
      <PageHeader onBack={() => onDone()} title="Log Rejects"
        subtitle={<>
          {run.run_number} · {run.grower} · {run.variety}
          {run.puc && <> · <span className="font-mono">{run.puc}</span></>}
        </>} />

      <div className="flex-1 p-4 space-y-5 overflow-y-auto pb-8">
        {/* U05-D1 — the run left the open list (a manager completed it) while
            this form was open. The form stays, with what was typed. Log is
            refused: rejects are logged on open runs only. */}
        {runClosed && (
          <div role="alert" className="rounded-xl bg-amber-900/40 border border-amber-700 px-4 py-3 text-sm text-amber-200">
            {run.run_number} was completed while you were logging. Your entry is still here.
            Rejects can only be logged on an open run, so it cannot be saved. Write the weight down and ask a manager.
          </div>
        )}
        {newerRun && <NewerRunNotice run={newerRun} current={run.run_number} />}
        {/* Kind: REJECT (sellable / collectible) vs WASTE (dump). */}
        <div>
          <label className="label">Kind <span className="text-danger">*</span></label>
          <div className="grid grid-cols-2 gap-2">
            {([
              ['REJECT', 'Reject — to bin', 'bg-amber-700'],
              ['WASTE',  'Waste — dump',     'bg-red-800'],
            ] as const).map(([v, label, c]) => (
              <button key={v} type="button"
                className={clsx('rounded-xl py-3 text-sm font-semibold',
                  kind === v ? `${c} text-on-fill` : 'bg-raised text-fg-secondary hover:brightness-110')}
                onClick={() => { setKind(v); setBinChoice('NEW_BIN') }}>
                {label}
              </button>
            ))}
          </div>
          <p className="text-caption text-fg-muted mt-1">
            {kind === 'REJECT'
              ? 'Goes into a numbered bin → stall / juice / farmer collect.'
              : 'Goes into a numbered waste bin → dump / disposal.'}
          </p>
        </div>

        {/* Bin picker — same UI for REJECT + WASTE. The bin row is keyed by
            kind so an OPEN reject bin and an OPEN waste bin are tracked
            independently; the mutation passes `kind` through to the new
            reject_bins row. */}
        {(kind === 'REJECT' || kind === 'WASTE') && (
          <div ref={binSectionRef}>
            <label className="label">Bin</label>
            <div className="space-y-2">
              {openBins.map(b => (
                <div key={b.reject_bin_id}
                  className={clsx('w-full rounded-xl py-1 px-1 text-sm flex items-center gap-1 transition-colors',
                    binChoice === b.reject_bin_id
                      ? (kind === 'WASTE' ? 'bg-red-700 text-on-fill' : 'bg-amber-700 text-on-fill')
                      : 'bg-raised text-fg-secondary')}>
                  <button type="button"
                    aria-pressed={binChoice === b.reject_bin_id}
                    className="tap-target-y flex-1 py-1.5 px-2 text-left font-medium hover:opacity-90"
                    onClick={() => setBinChoice(b.reject_bin_id)}>
                    <span className="font-mono">{b.bin_number}</span>
                    <span className="text-xs ml-2 opacity-80">{Number(b.weight_kg).toFixed(1)} kg so far</span>
                  </button>
                  <button type="button"
                    onClick={() => closeBin.mutate(b)}
                    disabled={closeBin.isPending || b.weight_kg <= 0}
                    aria-label={`Close bin ${b.bin_number} — marks it READY`}
                    title={b.weight_kg <= 0 ? 'Add weight first before closing' : 'Close this bin → READY'}
                    className="tap-target-y shrink-0 px-2 py-1.5 rounded-lg text-xs font-semibold bg-black/20 hover:bg-black/40 disabled:opacity-40 disabled:hover:bg-black/20 flex items-center gap-1">
                    <CheckCircle size={12} /> Close
                  </button>
                </div>
              ))}
              {closeError && (
                <p className="text-xs text-red-300 bg-red-950/40 border border-red-800 rounded px-2 py-1.5">
                  {closeError}
                </p>
              )}
              {/* A failed read must not become an invitation to start a second
                  bin for the same run: "no open bins" and "couldn't check for
                  open bins" look identical, and NEW_BIN is the default choice,
                  so the weighed kg would land under a duplicate number (REJ-36). */}
              {openBinsState === 'loading' && (
                <p className="text-xs text-fg-muted">Checking for open bins…</p>
              )}
              {openBinsState === 'unread' && (
                <div className="rounded-xl bg-amber-950/40 border border-amber-800 px-3 py-2 text-xs text-amber-200">
                  <p className="font-semibold">
                    {openBinsOffline ? 'No signal — open bins not checked.' : "Couldn't check for open bins."}
                  </p>
                  <p className="mt-0.5 opacity-90">
                    Starting a new bin now could duplicate one that is already open, so it waits until the open bins load.
                  </p>
                  <button type="button" onClick={() => refetchOpenBins()}
                    className="tap-target-y mt-1.5 underline font-semibold">
                    Try again
                  </button>
                </div>
              )}
              <button type="button"
                disabled={openBinsUnread}
                className={clsx('w-full rounded-xl py-2.5 px-3 text-left text-sm font-medium transition-colors',
                  binChoice === 'NEW_BIN'
                    ? 'bg-amber-700 text-on-fill'
                    : 'bg-raised text-fg-secondary hover:brightness-110',
                  openBinsUnread && 'opacity-40 cursor-not-allowed hover:bg-raised')}
                onClick={() => setBinChoice('NEW_BIN')}>
                + Start a new bin
              </button>
              {binChoice === 'NEW_BIN' && (
                <div className="pl-2 pt-1">
                  <label className="label text-xs">{isBerry ? 'Crate' : 'Container'} <span className="text-danger">*</span></label>
                  {isBerry ? (
                    /* ContainerTypePicker deliberately omits berry crates, so
                       berries pick from CRATE_TYPES directly (as intake does). */
                    <select className="field"
                      value={decodeContainerPickerValue(newBinPick, []).container_type ?? berryCrateFor(manifest.commodityName)}
                      onChange={e => setNewBinPick(encodeEnum(e.target.value as ContainerType))}>
                      {CRATE_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  ) : (
                    <>
                      <ContainerTypePicker
                        value={newBinPick}
                        onChange={setNewBinPick}
                        growerId={runGrowerId}
                      />
                      <p className="text-caption text-fg-muted mt-1">
                        Includes this grower's custom bin types from Bin Inventory.
                      </p>
                    </>
                  )}
                </div>
              )}
              {binChoice === 'NEW_BIN' && (
                <div className="pl-2" role="group" aria-label="Is this bin full?">
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      [true,  'Full, ready to send'],
                      [false, 'Still filling'],
                    ] as const).map(([v, label]) => (
                      <button key={label} type="button" aria-pressed={fullBin === v}
                        className={clsx('tap-target-y rounded-xl px-2 py-2 text-sm font-semibold',
                          fullBin === v ? 'bg-accent text-on-fill' : 'bg-raised text-fg-secondary hover:brightness-110')}
                        onClick={() => setFullBin(v)}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="text-caption text-fg-muted mt-1">
                    {fullBin
                      ? 'Log closes the bin. It is then Ready for the stall, a buyer or the dump.'
                      : 'The bin stays open. Add more weighings to it later.'}
                  </p>
                </div>
              )}
              <button type="button"
                className={clsx('w-full rounded-xl py-2.5 px-3 text-left text-xs text-fg-secondary hover:text-fg',
                  binChoice === 'NONE' ? 'bg-raised' : 'hover:bg-raised')}
                onClick={() => setBinChoice('NONE')}>
                Don't bin (track weight only)
              </button>
            </div>
          </div>
        )}

        {/* Source — citrus: three fixed stage chips; berry: a Field/Packline
            combo you can extend by typing (mig 1046). */}
        <div {...issues.box('source')}>
          <label className="label">Reject Source <span className="text-danger">*</span></label>
          {isBerry ? (
            <>
              <ComboField id="berry-reject-source" value={source} onChange={setSource}
                options={berrySourceOpts} placeholder="e.g. Field, Packline — or type a new one" />
              <p className="text-caption text-fg-muted mt-1">Type a new stage and it's remembered for next time.</p>
            </>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {REJECT_SOURCES.map(s => (
                <button key={s.value} type="button"
                  className={clsx('rounded-xl py-3 text-sm font-semibold transition-colors',
                    source === s.value ? `${s.colour} text-on-fill` : 'bg-raised text-fg-secondary hover:brightness-110')}
                  onClick={() => setSource(s.value)}>
                  {s.label}
                </button>
              ))}
            </div>
          )}
          {issues.msg('source')}
        </div>

        {/* Lot — which tipped pallet / delivery the rejected fruit came from
            (mig 20261019100000), so rejects read per pallet on the run's Lots
            view. Latest tip by default; nothing to pick until a tip exists. */}
        <CurrentLotChip tips={lot.tips} choice={lot.choice} onChange={lot.setChoice} noun="reject" />

        {/* Weight — mig 285 adds an opt-in gross toggle. When ON and the
            operator is creating a NEW bin, the gross weight is captured
            and the system subtracts the bin type's tare for net. When
            OFF, the original net-entry workflow is unchanged. */}
        <div {...issues.box('weight')}>
          <div className="flex items-end justify-between gap-2 mb-1">
            <label className="label mb-0">
              {grossMode && binChoice === 'NEW_BIN' ? 'Gross weight (kg)' : 'Weight (kg)'} <span className="text-danger">*</span>
            </label>
            {binChoice === 'NEW_BIN' && (
              <button type="button"
                onClick={() => setGrossMode(v => !v)}
                aria-pressed={grossMode}
                className={clsx('tap-target-y text-caption uppercase tracking-wider px-2 py-0.5 rounded transition-colors',
                  grossMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-raised text-fg-secondary hover:brightness-110')}
                title="Enter gross weight on scale; system subtracts the bin tare for net">
                {grossMode ? 'Weighed with bin: on' : 'Weighed with bin: off'}
              </button>
            )}
          </div>
          {grossMode && binChoice === 'NEW_BIN' ? (
            <>
              <input type="text" inputMode="decimal"
                className="field text-2xl font-bold"
                placeholder="0.00"
                value={grossKg} onChange={e => setGrossKg(safeDecimal(e.target.value))} />
              {tareUnknown ? (
                <p className="text-caption text-amber-300 mt-1" role="alert">
                  {binTypesLoading
                    ? 'Loading the bin tare…'
                    : binTypesError
                      ? 'Bin types did not load, so the tare is not known. Try again before you log.'
                      : 'No tare is set for this container. Turn off Weighed with bin and enter the net weight, or ask an admin to set it under Config → Storage → Bin types.'}
                  {binTypesError && (
                    <button type="button" className="ml-2 underline min-h-[44px]"
                      onClick={() => { void refetchBinTypes() }}>Try again</button>
                  )}
                </p>
              ) : dolly.on ? null : (
                <p className="text-caption text-fg-muted mt-1 tabular-nums">
                  {resolvedTareKg === 0 && (
                    <span className="block text-amber-300" role="status">This container has a tare of 0 kg, so net equals gross.</span>
                  )}
                  Tare {binsForCalc} × {perBinTareKg.toFixed(1)} = {totalTareKg.toFixed(1)} kg ·{' '}
                  <span className="text-emerald-300 font-semibold">
                    Net {(computedNetKg ?? 0).toFixed(1)} kg
                  </span>
                </p>
              )}
              {isBerry && !tareUnknown && <DollyPanel dolly={dolly} grossKg={grossKg} crates={binCount} crateTareKg={perBinTareKg} />}
            </>
          ) : (
            <input type="text" inputMode="decimal"
              className="field text-2xl font-bold"
              placeholder="0.00"
              value={weightKg} onChange={e => setWeightKg(safeDecimal(e.target.value))} />
          )}
          {issues.msg('weight')}
        </div>

        {/* Bin count.
            type="text" instead of type="number" deliberately: mouse-wheel
            scroll on a focused <input type="number"> silently changes the
            value by ±1 per scroll click, which has been mis-saving 17 → 16
            on the floor without operators noticing. inputMode="numeric" +
            pattern still surfaces the numeric keypad on mobile, and the
            onChange filter strips anything that isn't a digit. */}
        <div>
          <label className="label">{isBerry ? 'Crates' : 'Bin Count (optional)'}{isBerry && <span className="text-fg-muted normal-case"> · drives the tare</span>}</label>
          <input type="text" inputMode="numeric" pattern="[0-9]*"
            className="field"
            placeholder={isBerry ? 'e.g. 4' : 'e.g. 2'}
            value={binCount}
            onChange={e => setBinCount(e.target.value.replace(/[^0-9]/g, ''))} />
        </div>

        {/* Whole cartons condemned unopened — only when the lot is a received
            (prepacked) pallet. The kg above already includes them. */}
        {lot.effective?.lot_key.startsWith('pallet:') && (
          <div>
            <label className="label" htmlFor="reject-whole-cartons">Whole cartons condemned (optional)<span className="text-muted normal-case"> · unopened cartons in this entry; the kg already includes them</span></label>
            <input id="reject-whole-cartons" type="text" inputMode="numeric" pattern="[0-9]*"
              className="field"
              placeholder="e.g. 3"
              value={wholeCartons}
              onChange={e => setWholeCartons(e.target.value.replace(/[^0-9]/g, ''))} />
          </div>
        )}

        {/* Defect reasons — citrus: fixed toggle chips; berry: free-text tags
            (berry defects vary by site and aren't the citrus set). */}
        <div>
          <label className="label">Defect Reasons (optional)</label>
          {isBerry ? (
            <TagInput id="berry-reject-defects" value={defects} onChange={setDefects}
              placeholder="Type a reason + Enter (e.g. soft, mould, split)" />
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {DEFECT_REASONS.map(d => (
                <button key={d} type="button"
                  className={clsx('rounded-xl py-2.5 px-3 text-xs font-medium text-left transition-colors',
                    defects.includes(d)
                      ? 'bg-red-700 text-on-fill'
                      : 'bg-raised text-fg-secondary hover:brightness-110')}
                  onClick={() => toggleDefect(d)}>
                  {d}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="label">Notes (optional)</label>
          <textarea className="field text-sm resize-none" rows={2}
            placeholder="Any additional detail…"
            value={notes} onChange={e => setNotes(e.target.value)} />
        </div>

        {/* Admin-only backdate. Operators see nothing here; admins use
            it when logging historical / paper-trail rejects so the bin's
            created_at + reject_log's logged_at land on the correct day. */}
        {meIsAdmin && (
          <div>
            <label className="label flex items-center gap-2">
              <span>Date (admin · backdate)</span>
              {backdate && (
                <button type="button" onClick={() => setBackdate('')}
                  className="text-caption text-amber-300 hover:text-amber-200 underline">
                  clear
                </button>
              )}
            </label>
            <input type="date" className="field text-sm"
              value={backdate} onChange={e => setBackdate(e.target.value)}
              max={sastTodayYMD()} />
            <p className="text-caption text-fg-muted mt-1">
              Leave blank to log against today. Admin-only — used for historical / paper-trail rejects.
            </p>
          </div>
        )}

        {/* Photo */}
        <div>
          <label className="label">Bin Photo (optional)</label>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={e => {
              const file = e.target.files?.[0] ?? null
              setPhotoFile(file)
              setPhotoPreview(file ? URL.createObjectURL(file) : null)
            }} />
          {photoPreview ? (
            <div className="relative rounded-xl overflow-hidden">
              <img src={photoPreview} alt="Reject bin" className="w-full max-h-48 object-cover" />
              <button onClick={() => { setPhotoFile(null); setPhotoPreview(null) }}
                aria-label="Remove the reject-bin photo"
                className="tap-target absolute top-2 right-2 bg-canvas/70 text-fg rounded-full p-1.5">✕</button>
            </div>
          ) : (
            <button type="button" onClick={() => cameraRef.current?.click()}
              className="w-full rounded-xl border-2 border-dashed border-line py-5 flex flex-col items-center gap-2 text-fg-muted hover:border-red-500 hover:text-red-400 transition-colors">
              <Camera size={24} />
              <span className="text-sm">Photo of reject bin</span>
            </button>
          )}
        </div>

        {issues.live}
        {error && (
          <div className="rounded-xl bg-red-900/50 border border-red-700 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* F06 UF-17: Log stays pinned at the foot of the screen, above the
            keyboard, instead of 1,800px down the form. */}
        <div className="sticky bottom-0 z-10 -mx-4 px-4 py-3 bg-canvas border-t border-line-subtle">
        <button
          className={clsx(
            'w-full rounded-xl py-4 text-on-fill font-bold text-lg active:scale-95 transition-[transform,background-color,opacity] disabled:opacity-50',
            kind === 'WASTE'
              ? 'bg-red-700 hover:bg-red-600'
              : 'bg-amber-700 hover:bg-amber-600',
          )}
          disabled={logMutation.isPending || runClosed || grossBlocked || dolly.blocked
            // REJ-36: never start a bin over an unread open-bin list.
            || (binChoice === 'NEW_BIN' && openBinsUnread)}
          onClick={() => { if (issues.reveal()) logMutation.mutate() }}>
          {logMutation.isPending
            ? 'Logging…'
            : kind === 'WASTE' ? '✓ Log Waste' : '✓ Log Rejects'}
        </button>
        </div>

        <SubmitOverlay
          isPending={logMutation.isPending}
          isSuccess={logMutation.isSuccess}
          isError  ={logMutation.isError}
          error    ={logMutation.error}
          successLabel={kind === 'WASTE' ? 'Waste logged' : 'Rejects logged'}
          domain={REJECT_BIN_DOMAIN}
          onDismiss={() => logMutation.reset()}
        />
        <MutationOverlays of={[[onlyWhenDone(closeBin), 'Bin closed']]} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Rejects page — lists reject_log entries for the active run
// ---------------------------------------------------------------------------
// The Done banner when there is no bin number to show: queued offline, no bin, or the read-back is pending.
function LoggedBannerNotice({ logged, failed, onDismiss, actions }: {
  logged: LoggedReject; failed: boolean; onDismiss: () => void; actions: React.ReactNode
}) {
  if (logged.queued) {
    return (
      <SavedBanner title="Saved on this tablet" onDismiss={onDismiss} actions={actions}>
        <p>Not sent yet. The bin number shows here when it syncs. Do not log this weighing again.</p>
        {logged.fullQueued && <p>The bin stays open until it syncs. Then close it from the Filling now list.</p>}
      </SavedBanner>
    )
  }
  if (!logged.binId) {
    return (
      <SavedBanner title="Reject logged" onDismiss={onDismiss} actions={actions}>
        <p>{logged.kg.toFixed(1)} kg, no bin.</p>
      </SavedBanner>
    )
  }
  return (
    <SavedBanner title="Bin logged" onDismiss={onDismiss} actions={actions}>
      <p>{failed
        ? 'Saved. The bin number did not load. Find it in the list below.'
        : 'Reading the bin number…'}</p>
    </SavedBanner>
  )
}

// The lines under the bin number in the Done banner.
function LoggedBinNotes({ logged, bin, closeError }: {
  logged: LoggedReject; bin: CloseableBin & { status: string }; closeError: string | null
}) {
  const isOpen = bin.status === 'OPEN'
  return (
    <>
      <p>Write this number on the bin. {bin.weight_kg.toFixed(1)} kg in the bin{isOpen ? ' so far' : ''}.</p>
      {!isOpen && logged.closed && <p>Bin closed. It is Ready to send.</p>}
      {isOpen && logged.closeFailed && (
        <p role="alert" className="text-warning">Logged. {bin.bin_number} is still open. Tap Close bin to finish.</p>
      )}
      {closeError && <p role="alert" className="text-danger">{closeError}</p>}
    </>
  )
}

// U05 UF-04 — the Done banner after Log. Online it reads the bin back so the
// number on screen is the number on the server; offline the number does not
// exist yet, so it says "Saved on this tablet". Not save feedback: SubmitOverlay
// owns that.
function LoggedBanner({ logged, onDismiss, onLogAnother }: {
  logged: LoggedReject; onDismiss: () => void; onLogAnother: () => void
}) {
  const qc = useQueryClient()
  const binQ = useQuery({
    queryKey: ['reject-bin-logged', logged.binId],
    enabled: !logged.queued && !!logged.binId,
    queryFn: async () => {
      const { data, error } = await supabase.from('reject_bins')
        .select('reject_bin_id, bin_number, status, weight_kg, version')
        .eq('reject_bin_id', logged.binId!).maybeSingle()
      if (error) throw error
      return data as (CloseableBin & { status: string }) | null
    },
  })
  const [closeError, setCloseError] = useState<string | null>(null)
  const close = useMutation({
    mutationFn: async (bin: CloseableBin) => { setCloseError(null); return closeRejectBinAsked(bin) },
    onSuccess: (done) => {
      if (!done) return
      void qc.invalidateQueries({ queryKey: ['reject-bin-logged', logged.binId] })
      void qc.invalidateQueries({ queryKey: ['reject-bins'] })
      void qc.invalidateQueries({ queryKey: ['reject-bins-open'] })
    },
    onError: (e: Error) => setCloseError(friendlyError(e, REJECT_BIN_DOMAIN).message),
  })
  const another = <SavedBannerButton onClick={onLogAnother}>Log another</SavedBannerButton>

  const bin = binQ.data
  if (logged.queued || !logged.binId || !bin) {
    return <LoggedBannerNotice logged={logged} failed={binQ.isError || (binQ.isSuccess && !bin)}
      onDismiss={onDismiss} actions={another} />
  }
  const isOpen = bin.status === 'OPEN'
  return (
    <SavedBanner title="Bin logged" reference={bin.bin_number} onDismiss={onDismiss}
      actions={<>
        {another}
        {isOpen && (
          <SavedBannerButton disabled={close.isPending} onClick={() => close.mutate(bin)}>Close bin</SavedBannerButton>
        )}
      </>}>
      <LoggedBinNotes logged={logged} bin={bin} closeError={closeError} />
      <MutationOverlays of={[[onlyWhenDone(close), 'Bin closed']]} />
    </SavedBanner>
  )
}

// F06 UF-02 - the bins being filled on this run, both kinds, with the two
// things an operator does next: add a weighing, or close. Before this, an open
// bin showed only inside the Log form, and a waste bin needed a Kind tap first.
type FillingBin = CloseableBin & { kind: 'REJECT' | 'WASTE' }

// F06 UF-03 - "RUN-0418 started at 10:40 · Sunfield Citrus · Navel. Still
// logging on RUN-0415?" Switch and Stay; nothing changes by itself. In the Log
// form there is no Switch (it would drop what was typed), only the sentence.
function NewerRunNotice({ run, current, onSwitch, onStay }: {
  run: Run; current: string; onSwitch?: () => void; onStay?: () => void
}) {
  const at = new Date(run.started_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })
  return (
    <div role="status" className="rounded-xl bg-warning-soft border border-warning-edge px-3 py-2 text-callout text-warning">
      <p>{run.run_number} started at {at} · {run.grower} · {run.variety}. Still logging on {current}?</p>
      {onSwitch && onStay && (
        <div className="mt-2 flex gap-2">
          <Button variant="secondary" size="sm" onClick={onSwitch}>Switch to {run.run_number}</Button>
          <Button variant="ghost" size="sm" onClick={onStay}>Stay on {current}</Button>
        </div>
      )}
    </div>
  )
}

function FillingNow({ runId, onAdd }: { runId: string; onAdd: (b: FillingBin) => void }) {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['reject-bins-open', runId, 'all'],
    queryFn: async () => {
      const { data, error } = await supabase.from('reject_bins')
        .select('reject_bin_id, bin_number, kind, weight_kg, version')
        .eq('run_id', runId).eq('status', 'OPEN')
        .is('deleted_at', null).order('created_at')
      if (error) throw error
      return (data ?? []) as FillingBin[]
    },
    refetchInterval: POLL.live,
  })
  const [closeError, setCloseError] = useState<string | null>(null)
  const close = useMutation({
    // true = wrote, false = the operator backed out of the confirm.
    mutationFn: async (bin: FillingBin) => { setCloseError(null); return closeRejectBinAsked(bin) },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reject-bins-open'] })
      void qc.invalidateQueries({ queryKey: ['reject-bins'] })
      void qc.invalidateQueries({ queryKey: ['reject-bin-logged'] })
    },
    onError: (e: Error) => setCloseError(friendlyError(e, REJECT_BIN_DOMAIN).message),
  })
  const state = readState(q)
  const bins = q.data ?? []
  // Nothing to say while loading, and nothing to claim on a failed read.
  if (state === 'loading') return null
  if (state !== 'usable') {
    return (
      <div role="status" className="rounded-xl bg-warning-soft border border-warning-edge px-3 py-2 text-caption text-warning">
        Could not check which bins are open.{' '}
        <button type="button" onClick={() => void q.refetch()} className="tap-target-y underline font-semibold">Try again</button>
      </div>
    )
  }
  if (bins.length === 0) return null
  return (
    <Card className="space-y-2" aria-label="Filling now">
      <p className="text-caption uppercase tracking-wider text-fg-muted font-semibold">Filling now</p>
      {bins.map(b => (
        <div key={b.reject_bin_id} className="flex items-center gap-2 flex-wrap">
          <span className="font-mono font-semibold text-fg">{b.bin_number}</span>
          <span className="text-caption text-fg-muted">{b.kind === 'WASTE' ? 'Waste' : 'Reject'}</span>
          <span className="text-caption text-fg-secondary tabular-nums">{Number(b.weight_kg).toFixed(1)} kg so far</span>
          <span className="ml-auto flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => onAdd(b)}
              aria-label={`Add weighing to bin ${b.bin_number}`}>Add weighing</Button>
            <Button variant="ghost" size="sm" loading={close.isPending && close.variables?.reject_bin_id === b.reject_bin_id}
              disabled={close.isPending || Number(b.weight_kg) <= 0}
              onClick={() => close.mutate(b)} aria-label={`Close bin ${b.bin_number}`}>Close</Button>
          </span>
        </div>
      ))}
      {closeError && <p role="alert" className="text-caption text-danger">{closeError}</p>}
      <MutationOverlays of={[[onlyWhenDone(close), 'Bin closed']]} />
    </Card>
  )
}

export default function RejectsPage() {
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const [view, setView] = useState<'list' | 'new'>('list')
  // Persisted so navigating in/out of a reject keeps you on the run.
  // F06 UF-03: the pick is remembered for TODAY only (SAST). A pick from
  // yesterday reads as no pick, so the operator chooses the run on the line now.
  const [rawPickedRunId, setRawPickedRunId] = useTenantPersistedState<string | null>('kf49.rejects.pickedRunId', null)
  const [pickedDay, setPickedDay] = useTenantPersistedState<string | null>('kf49.rejects.pickedDay', null)
  const pickedRunId = pickedDay === sastTodayYMD() ? rawPickedRunId : null
  const setPickedRunId = (id: string | null) => { setRawPickedRunId(id); setPickedDay(id ? sastTodayYMD() : null) }
  // A newer run the operator chose to stay away from (Stay on the notice).
  const [stayedAwayFrom, setStayedAwayFrom] = useState<string | null>(null)
  const formRunRef = useRef<Run | null>(null)
  // U05 UF-04: the Done banner. Stays until dismissed or the next log starts.
  const [logged, setLogged] = useState<LoggedReject | null>(null)
  // F06 UF-02: "Add weighing" on a Filling now bin opens the form on that bin.
  const [startOn, setStartOn] = useState<{ binId: string; kind: 'REJECT' | 'WASTE' } | null>(null)
  // U05 UF-03: /rejects?run=<id> (from the Complete Run dialog) opens that run.
  const [searchParams, setSearchParams] = useSearchParams()
  const runParam = searchParams.get('run')
  // F06 UF-05: ?bin= (from an open bin's page) opens the form on that bin.
  const binParam = searchParams.get('bin')
  // The bin chip on an entry opens the bin only for someone the route lets in.
  const canOpenBins = useCanOpenModule('reject_bins')
  const [editTarget,    setEditTarget]    = useState<RejectEntry | null>(null)
  const { data: me } = useCurrentUser()
  const meIsAdmin = isAdmin(me?.role)
  const isBerry = useFlowManifest().usesForceCooling

  // Rejects can be logged on ACTIVE and TIPPING_COMPLETE runs. Operator must
  // tap a run before they see the rejects list — see PickRunFirst for why.
  const { data: rejectRuns = [], isLoading: runLoading, isError: runError, refetch: refetchRuns } = useQuery({
    queryKey: ['runs', 'selection', 'packing'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('runs').select('*')
        .in('status', [...PACKING_OPEN_STATUSES])
        .is('deleted_at', null)
        .order('started_at', { ascending: false })
      if (error) throw error
      return data as Run[]
    },
    refetchInterval: POLL.live,
    refetchOnWindowFocus: true,
  })

  // Apply ?run= once the open runs have loaded, then drop it from the URL. A
  // run that is not open falls through to the picker (the effect below clears it).
  useEffect(() => {
    if (!runParam || runLoading || runError) return
    const runOpen = rejectRuns.some(r => r.run_id === runParam)
    if (runOpen) setPickedRunId(runParam)
    setSearchParams(p => { p.delete('run'); p.delete('bin'); return p }, { replace: true })
    // A bin that is still open on that run: the form opens on it, kind carried.
    if (runOpen && binParam) {
      void (async () => {
        const { data, error } = await supabase.from('reject_bins')
          .select('reject_bin_id, kind, status, run_id').eq('reject_bin_id', binParam).maybeSingle()
        // A failed read leaves the form closed on the run: the operator taps Log Rejects.
        if (error) return
        const b = data as { reject_bin_id: string; kind: 'REJECT' | 'WASTE'; status: string; run_id: string | null } | null
        if (b && b.status === 'OPEN' && b.run_id === runParam) {
          setLogged(null); setStartOn({ binId: b.reject_bin_id, kind: b.kind }); setView('new')
        }
      })()
    }
  }, [runParam, runLoading, runError, rejectRuns])

  const activeRun = pickedRunId
    ? rejectRuns.find(r => r.run_id === pickedRunId) ?? null
    : null

  // Drop back to the runs list if the picked run becomes ineligible
  // (e.g. another device archived it).
  useEffect(() => {
    // U05-D1 — not while the Log form is open: it keeps its copy of the run.
    if (pickedRunId && !runLoading && view !== 'new') {
      const stillExists = rejectRuns.some(r => r.run_id === pickedRunId)
      if (!stillExists) {
        setPickedRunId(null)
        setView('list')
      }
    }
  }, [pickedRunId, runLoading, rejectRuns, view])

  const rejectsQ = useQuery({
    queryKey: ['rejects', activeRun?.run_id],
    enabled: !!activeRun,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reject_log')
        .select(`
          reject_id, run_id, reject_source, kind, weight_kg,
          gross_weight_kg, tare_kg, bin_count, defect_reasons, notes,
          photo_url, logged_at, reject_bin_id, version,
          disposition, disposition_at, disposition_notes,
          transferred_to_steel_cage,
          reject_bins:reject_bin_id ( bin_number )
        `)
        .eq('run_id', activeRun!.run_id)
        // Mig 355 — exclude soft-deleted rows from the operator-facing
        // list. They stay in the table for audit / recovery.
        .is('deleted_at', null)
        .order('logged_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as unknown as RejectEntry[]
    },
    refetchInterval: POLL.live,
  })
  // REJ-35 / STATE-64 — `data ?? []` with only isLoading checked meant a failed
  // or offline-paused read rendered "No rejects logged yet" DURING A LIVE RUN,
  // and drove the Rejects/Waste KPIs and the packout percentage to 0.0 kg. On
  // the floor that reads as a clean run, which is the one claim nobody
  // double-checks — and packout is what the grower is paid against.
  const rejects = rejectsQ.data ?? []
  const isLoading = rejectsQ.isLoading
  const rejectsUnknown = readState(rejectsQ) !== 'usable'

  // Client refs from the deliveries tipped into this run (mig 144 view).
  const { data: clientRefs = null } = useQuery<string | null>({
    queryKey: ['rejects-client-refs', activeRun?.run_id],
    enabled: !!activeRun,
    queryFn: async () => {
      const { data, error } = await supabase.from('runs_client_refs')
        .select('client_refs').eq('run_id', activeRun!.run_id).maybeSingle()
      if (error) throw error
      return (data?.client_refs ?? null) as string | null
    },
  })

  // Mig 355 — soft-delete. The sync trigger detects the deleted_at flip,
  // decrements the bin's weight, and cascade-soft-deletes the bin when
  // no live logs remain feeding it. Recoverable: an admin SQL UPDATE
  // setting deleted_at = NULL flows back through the same trigger and
  // restores the weight + un-cascades the bin.
  const deleteReject = useMutation({
    mutationFn: async (rejectId: string) => {
      const { data: { session } } = await supabase.auth.getSession()
      const userId = session?.user?.id ?? null
      const { error } = await supabase
        .from('reject_log')
        .update({
          deleted_at:     new Date().toISOString(),
          deleted_by:     userId,
          deleted_reason: 'Soft-deleted from /rejects',
        })
        .eq('reject_id', rejectId)
        .is('deleted_at', null)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rejects', activeRun?.run_id] })
      qc.invalidateQueries({ queryKey: ['runs'] })
      qc.invalidateQueries({ queryKey: ['reject-bins'] })
    },
    // Surface the mig-20260709120000 guard: if the bin is on a live dispatch,
    // the delete is refused with instructions to void the dispatch first
    // (audit §3.3) — previously this threw with no onError and failed silently.
    onError: (e: Error) => { void alertDialog({ title: 'Cannot delete this reject', message: friendlyError(e, REJECT_BIN_DOMAIN).message }) },
  })

  // U05-D1 — remember the run the form was opened on; if it leaves the open
  // list (completed by a manager) the form stays, with a notice.
  if (view === 'new' && activeRun) formRunRef.current = activeRun
  if (view !== 'new') formRunRef.current = null
  if (view === 'new' && (activeRun || formRunRef.current)) {
    return <NewRejectForm run={(activeRun ?? formRunRef.current)!} runClosed={!rejectRuns.some(r => r.run_id === formRunRef.current?.run_id)} startOn={startOn}
      newerRun={rejectRuns.find(r => r.started_at > (activeRun ?? formRunRef.current)!.started_at) ?? null}
      onDone={(l) => { setLogged(l ?? null); setView('list') }} />
  }

  // L1 — runs list. Operator must explicitly pick a run before logging rejects.
  if (!activeRun) {
    return (
      <PickRunFirst
        title="Rejects"
        runs={rejectRuns}
        isLoading={runLoading}
        isError={runError}
        onRetry={() => void refetchRuns()}
        onBack={() => navigate('/')}
        onPick={setPickedRunId}
        emptyTitle="No runs are open for reject logging"
        emptyHint="You can log rejects on runs that are Active or Tipping stopped."
        pickHint="Pick a run to log rejects against"
      />
    )
  }

  // F06 UF-03: a run started AFTER the picked one is probably the run on the
  // line now. Say so; change nothing by itself. rejectRuns is newest first.
  const newerRun = rejectRuns.find(r => r.run_id !== activeRun.run_id && r.started_at > activeRun.started_at) ?? null

  const rejectKindKg  = rejects.filter(r => r.kind !== 'WASTE').reduce((s, r) => s + r.weight_kg, 0)
  const wasteKindKg   = rejects.filter(r => r.kind === 'WASTE').reduce((s, r) => s + r.weight_kg, 0)
  const packoutPct    = activeRun?.packout_percent ?? null

  return (
    <div className="min-h-full bg-canvas flex flex-col">
      {/* PW-13: `title` is a plain string (it is the tab title too), so the
          run number — still the link to its run — leads the subtitle line, the
          same run line Log Rejects shows. */}
      <PageHeader onBack={() => { setPickedRunId(null); setView('list') }} title="Rejects"
        subtitle={<>
          <Link to={`/runs/${activeRun.run_id}`} className="hover:underline" title="Open the run">{activeRun.run_number}</Link>
          {' · '}{activeRun.grower} · {activeRun.variety}
        </>}
        badge={<RunStatusBadge status={activeRun.status} size="xs" />}
        actions={
          <Button variant="primary" size="sm" icon={<Plus size={16} />} className="coarse:hidden" onClick={() => { setLogged(null); setStartOn(null); setView('new') }}>
            Log
          </Button>
        } />

      <div className="flex-1 p-4 space-y-3 overflow-y-auto">
        {newerRun && newerRun.run_id !== stayedAwayFrom && (
          <NewerRunNotice run={newerRun} current={activeRun.run_number}
            onSwitch={() => { setLogged(null); setPickedRunId(newerRun.run_id) }}
            onStay={() => setStayedAwayFrom(newerRun.run_id)} />
        )}
        {logged && (
          <LoggedBanner logged={logged} onDismiss={() => setLogged(null)}
            onLogAnother={() => { setLogged(null); setStartOn(null); setView('new') }} />
        )}
        <FillingNow runId={activeRun.run_id}
          onAdd={(b) => { setLogged(null); setStartOn({ binId: b.reject_bin_id, kind: b.kind }); setView('new') }} />
        {/* Summary card */}
        {activeRun && (
          <Card className="space-y-3">
            {clientRefs && (
              <div className="flex items-center justify-between text-xs border-b border-line-subtle pb-2">
                <span className="text-fg-muted uppercase tracking-wider">Client ref(s)</span>
                <span className="text-fg font-mono">{clientRefs}</span>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              {/* REJ-35 — both are sums over the rejects read. When that read has
                  not landed they showed 0.0 kg beside a live run: a clean-run
                  claim, which is the one nobody double-checks. Packed comes
                  from the run row and is unaffected, so it keeps its figure. */}
              <div>
                <p className="text-xs text-fg-muted">Rejects</p>
                <p className="font-bold text-fg">
                  {rejectsUnknown ? <span className="text-muted">—</span> : `${rejectKindKg.toFixed(1)} kg`}
                </p>
              </div>
              <div>
                <p className="text-xs text-fg-muted">Waste</p>
                <p className="font-bold text-red-400">
                  {rejectsUnknown ? <span className="text-muted">—</span> : `${wasteKindKg.toFixed(1)} kg`}
                </p>
              </div>
              <div>
                <p className="text-xs text-fg-muted">Packed</p>
                <p className="font-bold text-fg">{activeRun.packed_weight_kg.toFixed(1)} kg</p>
              </div>
              <div>
                <p className="text-xs text-fg-muted">Packout</p>
                <p className={clsx('font-bold', packoutPct != null && packoutPct < 85 ? 'text-amber-400' : 'text-green-400')}>
                  {packoutPct != null ? `${packoutPct}%` : '—'}
                </p>
              </div>
            </div>

            {activeRun.tipped_net_weight_kg > 0 && rejectKindKg > 0 && (
              <div className="border-t border-line pt-3 space-y-2">
                {rejectBreakdownSources(rejects.map(r => r.reject_source), isBerry).map(src => {
                  // Same rule as the run page (Runs.tsx): Waste rows are not
                  // counted, and bars scale to the Rejects tile above (U05-D3).
                  const srcKg = rejects
                    .filter(r => r.reject_source === src.value && r.kind !== 'WASTE')
                    .reduce((s, r) => s + r.weight_kg, 0)
                  if (srcKg === 0) return null
                  const pct = (srcKg / activeRun.tipped_net_weight_kg * 100).toFixed(1)
                  const barW = Math.min(100, srcKg / rejectKindKg * 100)
                  return (
                    <div key={src.value}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-fg-secondary font-medium">{src.label}</span>
                        <span className="text-fg-muted">
                          {srcKg.toFixed(1)} kg · <span className="text-fg font-semibold">{pct}%</span> of tipped
                        </span>
                      </div>
                      <div className="h-1.5 bg-raised rounded-full overflow-hidden">
                        <div className={clsx('h-full rounded-full', src.colour)} style={{ width: `${barW}%` }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        )}

        {/* Loading / empty */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <BrandLoader className="w-8 h-8" />
          </div>
        )}

        {activeRun && rejects.length === 0 && (
          <QueryStateGate query={rejectsQ} noun="rejects for this run"
            empty={
              <div className="flex flex-col items-center justify-center py-16 text-fg-muted text-center">
                <AlertTriangle size={48} className="mb-3 opacity-40" />
                <p>No rejects logged yet</p>
                <p className="text-sm mt-1">Tap Log to record a reject bin.</p>
              </div>
            } />
        )}

        {/* Reject entries. F06 UF-17: two columns from tablet width up. */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {rejects.map(r => {
          const srcCfg = sourceChip(r.reject_source)
          const isWaste = r.kind === 'WASTE'
          const binNumber = r.reject_bins?.bin_number ?? null
          // Gross-mode entries carry both gross + tare; surface the breakdown
          // so the operator can sanity-check net = gross − tare on the floor.
          const grossLine = r.gross_weight_kg != null && r.tare_kg != null
            ? `${r.gross_weight_kg.toFixed(1)} gross − ${r.tare_kg.toFixed(1)} tare`
            : null
          return (
            <Card key={r.reject_id} className={clsx('space-y-2', isWaste && '!border-red-800/50')}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={clsx('font-bold text-lg', isWaste ? 'text-red-400' : 'text-fg')}>
                    {r.weight_kg.toFixed(1)} kg
                    <span className="text-caption uppercase tracking-wider text-fg-muted font-semibold ml-1">net</span>
                  </p>
                  <span className={clsx(
                    'text-caption uppercase tracking-wider rounded-full px-2 py-0.5 font-bold',
                    isWaste
                      ? 'bg-red-900/60 text-red-300'
                      : 'bg-raised text-fg-secondary',
                  )}>
                    {isWaste ? 'Waste' : 'Reject'}
                  </span>
                  {srcCfg && (
                    <span className={clsx('text-xs rounded-full px-2 py-0.5 font-semibold bg-raised text-fg-secondary')}>
                      {srcCfg.label}
                    </span>
                  )}
                  {binNumber && (canOpenBins === true && r.reject_bin_id ? (
                    <Link to={`/reject-bins/${r.reject_bin_id}`} aria-label={`Open bin ${binNumber}`}
                      className="text-xs font-mono rounded-full px-2 py-0.5 bg-raised/80 text-fg-secondary inline-flex items-center gap-1 min-h-[44px] hover:underline">
                      <Package size={11} /> {binNumber}
                    </Link>
                  ) : (
                    <span className="text-xs font-mono rounded-full px-2 py-0.5 bg-raised/80 text-fg-secondary inline-flex items-center gap-1">
                      <Package size={11} /> {binNumber}
                    </span>
                  ))}
                  {r.bin_count != null && (
                    <span className="text-xs text-fg-muted">{r.bin_count} {isBerry ? 'crate' : 'bin'}{r.bin_count !== 1 ? 's' : ''}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <p className="text-xs text-fg-muted">
                    {new Date(r.logged_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {meIsAdmin && (
                    <>
                      <button
                        onClick={() => setEditTarget(r)}
                        aria-label={`Edit the ${r.weight_kg.toFixed(1)} kg reject logged at ${new Date(r.logged_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}`}
                        title="Edit reject (admin)"
                        className="tap-target p-1.5 rounded-lg text-fg-muted hover:text-amber-400 hover:bg-raised transition-colors">
                        <Pencil size={14} />
                      </button>
                      {/* Mig 355 — admin-only, confirmation-gated. The
                          delete is now a SOFT-DELETE that the sync
                          trigger picks up to decrement the bin's
                          weight + cascade-soft-delete the bin if it
                          becomes empty. Recoverable via admin SQL
                          (UPDATE reject_log SET deleted_at = NULL). */}
                      <button
                        onClick={async () => {
                          const summary = [
                            `Reject ${r.weight_kg.toFixed(1)} kg`,
                            srcCfg?.label ?? r.reject_source,
                            r.kind === 'WASTE' ? 'Waste' : null,
                            r.reject_bins?.bin_number ? `bin ${r.reject_bins.bin_number}` : null,
                          ].filter(Boolean).join(' · ')
                          const ok = await confirmDialog({
                            title:   'Delete this reject?',
                            message: `${summary}\n\nSoft-deletes the row + decrements the bin's weight. When the bin has no live logs left, it also disappears from the list. Recoverable via admin SQL.`,
                            confirm: 'Delete',
                            tone:    'danger',
                          })
                          if (ok) deleteReject.mutate(r.reject_id)
                        }}
                        aria-label={`Delete the ${r.weight_kg.toFixed(1)} kg ${srcCfg?.label ?? r.reject_source} reject${r.reject_bins?.bin_number ? ` from bin ${r.reject_bins.bin_number}` : ''}`}
                        title="Delete reject (admin · soft-delete)"
                        className="tap-target p-1.5 rounded-lg text-fg-muted hover:text-red-400 hover:bg-red-900/30 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {grossLine && (
                <p className="text-caption text-fg-muted tabular-nums">
                  {grossLine} = <span className="text-fg-secondary">{r.weight_kg.toFixed(1)} kg net</span>
                </p>
              )}

              {r.defect_reasons && r.defect_reasons.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {r.defect_reasons.map(d => (
                    <span key={d} className="text-xs bg-red-900/40 text-red-300 rounded-full px-2 py-0.5">{d}</span>
                  ))}
                </div>
              )}

              {r.notes && <p className="text-xs text-fg-muted">{r.notes}</p>}

              {r.photo_url && (
                <Photo bucket="delivery-photos" value={r.photo_url}
                  alt="Reject bin" className="w-full max-h-32 object-cover rounded-xl" />
              )}

              {/* F06 UF-19: no "Dispose" row. An entry that already has a
                  disposition keeps it as a read-only chip; the column stays. */}
              {r.disposition !== 'PENDING' && (
                <div className="flex items-center gap-2 pt-2 border-t border-line-subtle">
                  <span className={clsx(
                    'text-caption rounded-full px-2 py-0.5 border font-semibold',
                    DISPOSITION_CFG[r.disposition].pill,
                  )}>
                    {DISPOSITION_CFG[r.disposition].emoji} {DISPOSITION_CFG[r.disposition].label}
                  </span>
                  {r.disposition === 'MAGALIES' && r.transferred_to_steel_cage && (
                    <span className="text-caption text-fg-muted">in steel cage</span>
                  )}
                  {r.disposition_notes && (
                    <span className="text-caption text-fg-muted italic truncate">{r.disposition_notes}</span>
                  )}
                </div>
              )}
            </Card>
          )
        })}
        </div>
      </div>

      {editTarget && (
        <EditRejectDialog
          reject={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['rejects', activeRun?.run_id] })
            qc.invalidateQueries({ queryKey: ['runs'] })
            setEditTarget(null)
          }}
        />
      )}
      <CaptureFab label="Log" onClick={() => { setLogged(null); setStartOn(null); setView('new') }} />
    </div>
  )
}

// =============================================================================
// EditRejectDialog — admin amendment of a logged reject_log row. Lets the
// operator correct a mistyped weight, source, defect list, or note. The
// disposition columns are intentionally not exposed here.
// =============================================================================
// Already a large modal on main; this fix adds the gross-mode re-tare branch. A
// full modal-splitting refactor is out of scope for a weight-calc bug fix
// (tracked in REFACTOR-BACKLOG).
// fallow-ignore-next-line complexity
function EditRejectDialog({ reject, onClose, onSaved }: {
  reject:  RejectEntry
  onClose: () => void
  onSaved: () => void
}) {
  // Was this reject logged in gross mode (mig 285)? If so its net weight is
  // derived — net = gross − bin_count × per-bin tare — so changing the bin
  // count here must re-tare and recompute net, exactly like NewRejectForm.
  // Legacy net-entry rejects (no gross_weight_kg) keep net as a directly
  // editable field and treat bin_count as metadata only.
  const wasGross = reject.gross_weight_kg != null
  // Recover the per-bin tare from the stored total ÷ the bin count that
  // produced it. Deliberately NOT re-resolved from the bin type: we preserve
  // the exact tare applied at log time (the bin type's tare may have moved
  // since). bin_count can be null with tare applied for 1 bin, hence max(1,…).
  const manifest = useFlowManifest()
  const isBerry  = manifest.usesForceCooling
  // A berry dolly entry stores crates × crate tare + dolly in one tare number.
  // Split it with the CURRENT crate tare (the new form's source). Unknown crate
  // tare, or nothing left over: not a dolly, and everything works as before.
  const binTypesQ = useBinTypes({ onlyActive: true, enabled: isBerry && wasGross })
  const crateTareKg = isBerry && wasGross
    ? resolveTareKg({ containerType: berryCrateFor(manifest.commodityName), binTypeId: null, binTypes: binTypesQ.data ?? [] })
    : null
  const dollyKg = wasGross && isBerry
    ? splitStoredTare({ tareKg: reject.tare_kg, crates: reject.bin_count, crateTareKg })
    : null
  const perBinTareKg = wasGross
    ? (dollyKg !== null && crateTareKg !== null ? crateTareKg : (reject.tare_kg ?? 0) / Math.max(1, reject.bin_count ?? 1))
    : 0
  const { data: berrySourceOpts = BERRY_REJECT_SOURCES } = useBerryRejectSources(isBerry)

  const [grossKg,    setGrossKg]    = useState(wasGross ? String(reject.gross_weight_kg) : '')
  const [weightKg,   setWeightKg]   = useState(String(reject.weight_kg))
  const [binCount,   setBinCount]   = useState(reject.bin_count != null ? String(reject.bin_count) : '')
  const [source,     setSource]     = useState<string>(reject.reject_source ?? '')
  const [defects,    setDefects]    = useState<string[]>(reject.defect_reasons ?? [])
  const [notes,      setNotes]      = useState(reject.notes ?? '')
  const [error,      setError]      = useState<string | null>(null)

  // Live gross→net recompute — mirrors NewRejectForm so the operator watches
  // net re-tare as the bin count (or gross) changes. Defaults to 1 bin when
  // bin_count is blank, matching the create form.
  const binsForCalc   = Math.max(1, parseInt(binCount || '1', 10) || 1)
  const totalTareKg   = perBinTareKg * binsForCalc + (dollyKg ?? 0)
  const computedNetKg = wasGross ? Math.max(0, (parseFloat(grossKg) || 0) - totalTareKg) : null

  function toggleDefect(d: string) {
    setDefects(curr => curr.includes(d) ? curr.filter(x => x !== d) : [...curr, d])
  }

  const save = useMutation({
    // Gross-vs-net re-tare branch on top of the existing validate +
    // optimistic-write save. Kept inline (not extracted) to avoid duplicating
    // the tare math across the create + edit forms.
    // fallow-ignore-next-line complexity
    mutationFn: async () => {
      if (!source) throw new ValidationError('Pick a reject source')
      const bc = binCount.trim() === '' ? null : parseInt(binCount)
      if (bc !== null && (!Number.isFinite(bc) || bc < 0)) throw new ValidationError('Bin count must be ≥ 0')

      // Gross mode: re-derive net + tare from the (possibly edited) gross and
      // bin count so weight_kg / tare_kg / gross_weight_kg stay consistent.
      // Net mode: net is entered directly and there's no tare to re-apply.
      let weightPatch: { weight_kg: number; gross_weight_kg?: number; tare_kg?: number }
      if (wasGross) {
        if (isBerry && binTypesQ.isLoading) throw new ValidationError('Loading the crate tare. Try again in a moment.')
        const gross = parseFloat(grossKg)
        if (!Number.isFinite(gross) || gross <= 0) throw new ValidationError('Gross weight must be > 0 kg')
        if (dollyKg !== null) {
          const d = dollyNet({ grossKg: gross, crates: bc ?? 0, crateTareKg: perBinTareKg, dollyTareKg: dollyKg })
          if (!d.ok) throw new ValidationError(DOLLY_BLOCK_MESSAGE[d.reason])
        }
        const net = Math.max(0, gross - totalTareKg)
        if (net <= 0) {
          throw new ValidationError(`Gross ${gross.toFixed(1)} kg ≤ total tare ${totalTareKg.toFixed(1)} kg (${binsForCalc} × ${perBinTareKg.toFixed(1)}) — net would be zero`)
        }
        weightPatch = { weight_kg: net, gross_weight_kg: gross, tare_kg: totalTareKg }
      } else {
        const w = parseFloat(weightKg)
        if (!Number.isFinite(w) || w <= 0) throw new ValidationError('Weight must be > 0 kg')
        weightPatch = { weight_kg: w }
      }

      try {
        await updateWithVersion(
          'reject_log',
          { col: 'reject_id', value: reject.reject_id },
          reject.version,
          {
            ...weightPatch,
            bin_count:      bc,
            reject_source:  source,
            defect_reasons: defects.length > 0 ? defects : null,
            notes:          notes.trim() || null,
          },
        )
      } catch (e) {
        if (isStaleWriteError(e)) {
          throw new ValidationError('This reject was updated by someone else — refresh and try again.')
        }
        throw e
      }
    },
    onSuccess: onSaved,
    onError: (e: Error) => setError(friendlyError(e, REJECT_BIN_DOMAIN).message),
  })

  return (
    <Modal title="Edit reject" onClose={onClose} size="md"
      snapshot={[grossKg, weightKg, binCount, source, JSON.stringify(defects), notes].join('|')}
      onSubmit={() => save.mutate()} isPending={save.isPending}
      submitLabel="Save changes"
      overlay={{
        isPending:    save.isPending,
        isSuccess:    save.isSuccess,
        isError:      save.isError,
        error:        save.error,
        successLabel: 'Reject updated',
        onDismiss:    () => save.reset(),
      }}>
      <div className="space-y-3">
        <p className="text-caption text-fg-muted">
          Logged {new Date(reject.logged_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}
        </p>

        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="label">{wasGross ? 'Gross (kg)' : 'Weight (kg)'}</span>
            {wasGross ? (
              <input type="text" inputMode="decimal"
                value={grossKg} onChange={e => setGrossKg(safeDecimal(e.target.value))}
                className="field text-lg font-bold mt-1" />
            ) : (
              <input type="text" inputMode="decimal"
                value={weightKg} onChange={e => setWeightKg(safeDecimal(e.target.value))}
                className="field text-lg font-bold mt-1" />
            )}
          </label>
          <label className="block">
            <span className="label">{isBerry ? 'Crates' : 'Bin count'} <span className="text-fg-muted normal-case">(optional)</span></span>
            {/* type="text" + inputMode="numeric" — same fix as the New
                Reject form to dodge the wheel-scroll-decrements-by-1 trap. */}
            <input type="text" inputMode="numeric" pattern="[0-9]*"
              value={binCount}
              onChange={e => setBinCount(e.target.value.replace(/[^0-9]/g, ''))}
              className="field text-sm mt-1" />
          </label>
        </div>
        {/* Gross-mode live tare/net preview — mirrors NewRejectForm so the
            operator sees net re-tare as the bin count (or gross) changes. */}
        {dollyKg !== null && (
          <p className="text-caption text-fg-secondary" role="status">Weighed on a dolly · dolly {dollyKg.toFixed(1)} kg</p>
        )}
        {wasGross && (
          <p className="text-caption text-fg-muted tabular-nums">
            Tare {binsForCalc} × {perBinTareKg.toFixed(1)}{dollyKg !== null ? ` + dolly ${dollyKg.toFixed(1)}` : ''} = {totalTareKg.toFixed(1)} kg ·{' '}
            <span className="text-emerald-300 font-semibold">
              Net {(computedNetKg ?? 0).toFixed(1)} kg
            </span>
          </p>
        )}

        <div>
          <p className="label mb-1.5">Source</p>
          {isBerry ? (
            <ComboField id="berry-edit-source" value={source} onChange={setSource}
              options={berrySourceOpts} placeholder="e.g. Field, Packline" />
          ) : (
            <div className="grid grid-cols-3 gap-1.5">
              {REJECT_SOURCES.map(s => (
                <button key={s.value} type="button"
                  onClick={() => setSource(s.value)}
                  aria-pressed={source === s.value}
                  className={clsx(
                    'tap-target-y rounded-lg px-2 py-1.5 text-xs font-semibold border',
                    source === s.value
                      ? `${s.colour} border-white/40 text-on-fill`
                      : 'bg-canvas border-line text-fg-secondary hover:border-fg-muted',
                  )}>
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="label mb-1.5">Defect reasons</p>
          {isBerry ? (
            <TagInput id="berry-edit-defects" value={defects} onChange={setDefects}
              placeholder="Type a reason + Enter" />
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {DEFECT_REASONS.map(d => (
                <button key={d} type="button"
                  onClick={() => toggleDefect(d)}
                  aria-pressed={defects.includes(d)}
                  className={clsx(
                    'tap-target-y rounded-full px-2.5 py-1 text-caption font-medium border',
                    defects.includes(d)
                      ? 'bg-red-900/60 text-red-200 border-red-700'
                      : 'bg-canvas text-fg-muted border-line hover:border-fg-muted',
                  )}>
                  {d}
                </button>
              ))}
            </div>
          )}
        </div>

        <label className="block">
          <span className="label">Notes</span>
          <input value={notes} onChange={e => setNotes(e.target.value)}
            maxLength={2000} className="field text-sm mt-1" placeholder="Optional" />
        </label>

        {error && <p className="text-xs text-red-400">{error}</p>}
      </div>
    </Modal>
  )
}
